import { css } from "@emotion/react";
import React, { useState } from "react";
import { AlertMessageType, useAlert } from "../Alert";
import { Button } from "../Button";
import { Loading } from "../Loading";
import { sequence } from "../@utils/sequence";
import { CellError } from "./CSVImportUI";

/**
 * これはCSVImportUIのためにsumyca-manager-frontで作成されたものであり、
 * matsuri-uiのコンポーネントとして使用できません。
 */
export const SubmitProgress = <Row extends Record<string, string>>({
  data,
  errors,
  mode,
  onSubmit,
  onSubmitByRow,
  onSubmitComplete,
  onFinish,
  successMessage,
  label,
  onConfirm,
}: {
  data: Row[];
  errors: (CellError[] | undefined)[];
  mode: "all" | "byRow";
  onSubmit?: (data: Row[]) => Promise<{
    data?: Record<string, string>[];
    errorMessage?: AlertMessageType;
  }>;
  onSubmitByRow?: (
    index: number,
    row: Row,
  ) => Promise<{
    data?: Record<string, string>;
    /**
     * @deprecated 代わりに `errors` を使用してください。
     */
    error?: CellError;
    errors?: CellError[];
  }>;
  onSubmitComplete: (data: Row[], errors: (CellError[] | undefined)[]) => void;
  onFinish: () => void;
  successMessage?: string;
  label: string;
  onConfirm?: () => boolean;
}) => {
  const { throwAlert } = useAlert();
  const [processing, setProcessing] = useState(false);
  const [taskProcessed, setTaskProcessed] = useState(0);
  const submitRows = async () => {
    let noError = true;
    const dataCloned = structuredClone(data);
    const errorsCloned = structuredClone(errors);
    if (mode === "all") {
      const result = await onSubmit?.(data);

      onSubmitComplete(
        result?.data
          ? data.map((item, index) => {
              return {
                ...item,
                ...result.data?.[index],
              };
            })
          : data,
        [],
      );

      throwAlert(
        result?.errorMessage ? new Error("CSVImportUI Error") : undefined,
        {
          errorMessage: result?.errorMessage,
          successMessage,
          ignoreSuccess: !successMessage,
          onSuccess: () => {
            onFinish();
          },
        },
      );
    } else {
      await sequence(
        data.map((row, index) => async () => {
          const rowError = errors[index];
          if (rowError && rowError.length > 0) {
            noError = false;
            return;
          }

          if (onSubmitByRow) {
            const result = await onSubmitByRow(index, row);
            if (result.data) {
              dataCloned[index] = {
                ...dataCloned[index],
                ...result.data,
              };
            }
            const resultErrors = [
              ...(result.errors ?? []),
              ...(result.error ? [result.error] : []),
            ];
            if (resultErrors.length > 0) {
              noError = false;
              if (errorsCloned[index] === undefined) {
                errorsCloned[index] = resultErrors;
              } else {
                errorsCloned[index]?.push(...resultErrors);
              }
            }
          }

          setTaskProcessed((prev) => prev + 1);
        }) ?? [],
      );

      onSubmitComplete(dataCloned, errorsCloned);

      throwAlert(noError ? undefined : new Error("CSVImportUI Error"), {
        errorMessage: {
          happend: "CSVの内容にエラーがあります。",
          action:
            "ハイライトされた行の旗アイコンにホバーして、エラーの詳細を確認してください。",
          log: undefined,
        },
        successMessage,
        ignoreSuccess: !successMessage,
        onSuccess: () => {
          onFinish();
        },
      });
    }
  };

  return (
    <div
      css={css`
        display: flex;
        gap: 8px;
        align-items: center;
      `}
    >
      {processing && (
        <React.Fragment>
          <Loading />
          {mode === "all" ? data.length : taskProcessed} / {data.length}{" "}
          件処理中…
        </React.Fragment>
      )}
      <Button
        variant="filled"
        color="primary"
        disabled={processing || data.length === 0}
        onClick={async () => {
          if (onConfirm) {
            const ok = onConfirm();
            if (!ok) {
              return;
            }
          }

          setProcessing(true);

          await submitRows();

          setProcessing(false);
        }}
      >
        {label}
      </Button>
    </div>
  );
};
